import InsightsIcon from '@mui/icons-material/Insights';
import CampaignIcon from '@mui/icons-material/Campaign';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EventNoteIcon from '@mui/icons-material/EventNote';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ForumIcon from '@mui/icons-material/Forum';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ComputerIcon from '@mui/icons-material/Computer';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ExtensionIcon from '@mui/icons-material/Extension';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Navigate } from 'react-router-dom';
import { t } from 'i18next';
import { CrmKanban } from '@layouts/pages/CRM/kanban-board';
import { Agenda } from '@layouts/pages/CRM/agenda';
import { Users } from '@layouts/pages/System/users';
import { Profiles } from '@layouts/pages/System/profiles';
import { LeadPage } from '@layouts/pages/CRM/lead';
import { SalesGoalsPage } from '@layouts/pages/CRM/sales-goals';
import { Dashboard } from '@layouts/pages/Dashboard';
import { Campaigns } from '@layouts/pages/Dashboard/campaigns';
import { People } from '@layouts/pages/CRM/people';
import { PeopleImport } from '@layouts/pages/CRM/people/import';
import { Companies } from '@layouts/pages/CRM/companies';
import { Multichannel } from '@layouts/pages/Multichannel';
import { TikTokIcon } from '@components/TikTokIcon';
import { GENERAL_SETTINGS_SCREENS, GeneralSettings } from '@layouts/pages/Settings/general';
import { QuickReplies } from '@layouts/pages/Settings/quick-replies';
import { WhatsappTemplates } from '@layouts/pages/Settings/whatsapp-templates';
import { Automations } from '@layouts/pages/Settings/automations';
import { Integrations } from '@layouts/pages/Settings/integrations';
import { MetaIntegrations } from '@layouts/pages/Settings/integrations/meta';
import { ApiIntegrations } from '@layouts/pages/Settings/integrations/api';
import { TeamChatIntegrations } from '@layouts/pages/Settings/integrations/chat';
import { ChatwootIntegration } from '@layouts/pages/Settings/integrations/chatwoot';
import { AppRoute } from 'types/app-route';

const routes: AppRoute[] = [
    {
        type: 'collapse',
        name: t('sideNav.dashboard'),
        key: 'dashboard',
        icon: <InsightsIcon />,
        collapse: [
            {
                name: t('sideNav.visaoGeral'),
                key: 'visao-geral',
                permissions: 'Dashboard',
                route: '/dashboard',
                icon: <SpaceDashboardIcon />,
                component: <Dashboard />,
            },
            {
                name: t('sideNav.campanhas'),
                key: 'campanhas',
                permissions: 'Campaigns',
                route: '/dashboard/campanhas',
                icon: <CampaignIcon />,
                component: <Campaigns />,
            },
        ],
    },
    {
        type: 'collapse',
        name: t('sideNav.crm.crm'),
        key: 'crm',
        icon: <ViewKanbanIcon />,
        collapse: [
            {
                name: t('sideNav.crm.quadroKanban'),
                key: 'quadro-kanban',
                permissions: 'KanbanBoard',
                route: '/crm/quadro-kanban',
                icon: <ViewKanbanIcon />,
                component: <CrmKanban />,
            },
            {
                name: t('sideNav.crm.metas'),
                key: 'metas',
                permissions: 'SalesGoals',
                route: '/crm/metas',
                icon: <TrackChangesIcon />,
                component: <SalesGoalsPage />,
            },
            {
                // Fora do menu: só se chega aqui a partir de um card.
                show: false,
                name: t('telaLead.titulo'),
                key: 'lead-detalhe',
                menuKey: 'quadro-kanban',
                permissions: 'always',
                route: '/crm/lead/:leadId',
                icon: <ViewKanbanIcon />,
                component: <LeadPage />,
            },
            {
                name: t('sideNav.crm.agenda'),
                key: 'agenda',
                permissions: 'Agenda',
                route: '/crm/agenda',
                icon: <EventNoteIcon />,
                component: <Agenda />,
            },
            {
                name: t('sideNav.crm.pessoas'),
                key: 'pessoas',
                permissions: 'People',
                route: '/crm/pessoas',
                icon: <PeopleAltIcon />,
                component: <People />,
            },
            {
                name: t('sideNav.crm.empresas'),
                key: 'empresas',
                permissions: 'Companies',
                route: '/crm/empresas',
                icon: <BusinessIcon />,
                component: <Companies />,
            },
            {
                // Fora do menu: abre pelo botão Importar de Pessoas e pelo card de integrações.
                show: false,
                name: t('people.importar.titulo'),
                key: 'pessoas-importar',
                menuKey: 'pessoas',
                permissions: 'People',
                route: '/crm/pessoas/importar',
                icon: <PeopleAltIcon />,
                component: <PeopleImport />,
            },
            // Endereços antigos das telas movidas para Configurações → Gerais; ainda são usados.
            {
                show: false,
                name: t('sideNav.crm.statusLead'),
                key: 'status-lead',
                permissions: 'LeadStatuses',
                route: '/crm/status-lead',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais?aba=status" replace />,
            },
            {
                show: false,
                name: t('sideNav.crm.colunasPadrao'),
                key: 'colunas-padrao',
                permissions: 'DefaultColumns',
                route: '/crm/colunas-padrao',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais?aba=colunas" replace />,
            },
        ],
    },
    {
        type: 'collapse',
        name: t('sideNav.configuracoes.configuracoes'),
        key: 'configuracoes',
        icon: <SettingsIcon />,
        collapse: [
            {
                name: t('sideNav.configuracoes.gerais'),
                key: 'gerais',
                permissions: GENERAL_SETTINGS_SCREENS,
                route: '/configuracoes/gerais',
                icon: <ListAltIcon />,
                component: <GeneralSettings />,
            },
            {
                show: false,
                name: t('sideNav.configuracoes.gerais'),
                key: 'leads-e-quadros',
                permissions: GENERAL_SETTINGS_SCREENS,
                route: '/configuracoes/leads-e-quadros',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais" replace />,
            },
            {
                show: false,
                name: t('sideNav.configuracoes.motivosPerda'),
                key: 'motivos-perda',
                permissions: 'LossReasons',
                route: '/configuracoes/motivos-perda',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais?aba=motivos" replace />,
            },
            {
                show: false,
                name: t('sideNav.configuracoes.tags'),
                key: 'tags',
                permissions: 'Tags',
                route: '/configuracoes/tags',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais?aba=tags" replace />,
            },
            {
                show: false,
                name: t('sideNav.configuracoes.camposPersonalizados'),
                key: 'campos-personalizados',
                permissions: 'CustomFields',
                route: '/configuracoes/campos-personalizados',
                icon: <ListAltIcon />,
                component: <Navigate to="/configuracoes/gerais?aba=campos" replace />,
            },
            {
                name: t('sideNav.configuracoes.respostasRapidas'),
                key: 'respostas-rapidas',
                permissions: 'QuickReplies',
                route: '/configuracoes/respostas-rapidas',
                icon: <BoltIcon />,
                component: <QuickReplies />,
            },
            {
                name: t('sideNav.configuracoes.templatesWhatsapp'),
                key: 'templates-whatsapp',
                permissions: 'WhatsappTemplates',
                route: '/configuracoes/templates-whatsapp',
                icon: <ChatBubbleOutlineIcon />,
                component: <WhatsappTemplates />,
            },
            {
                name: t('sideNav.configuracoes.automacoes'),
                key: 'automacoes',
                permissions: 'Automations',
                route: '/configuracoes/automacoes',
                icon: <AutoModeIcon />,
                component: <Automations />,
            },
            {
                name: t('sideNav.configuracoes.integracoes'),
                key: 'integracoes',
                permissions: 'Integrations',
                route: '/configuracoes/integracoes',
                icon: <ExtensionIcon />,
                component: <Integrations />,
            },
            {
                // Fora do menu: só se chega aqui pelo card da Meta.
                show: false,
                name: t('sideNav.configuracoes.integracoes'),
                key: 'integracoes-meta',
                menuKey: 'integracoes',
                permissions: 'Integrations',
                route: '/configuracoes/integracoes/meta',
                icon: <ExtensionIcon />,
                component: <MetaIntegrations />,
            },
            {
                // Fora do menu: chega-se pelos cards de webhook, n8n/Zapier/Make e API.
                show: false,
                name: t('integracoes.apiEWebhooks'),
                key: 'integracoes-api',
                menuKey: 'integracoes',
                permissions: 'Integrations',
                route: '/configuracoes/integracoes/api',
                icon: <ExtensionIcon />,
                component: <ApiIntegrations />,
            },
            {
                // Fora do menu: só se chega aqui pelo card de avisos no chat da equipe.
                show: false,
                name: t('webhooks.tituloChat'),
                key: 'integracoes-chat',
                menuKey: 'integracoes',
                permissions: 'Integrations',
                route: '/configuracoes/integracoes/chat',
                icon: <ExtensionIcon />,
                component: <TeamChatIntegrations />,
            },
            {
                // Fora do menu: só se chega aqui pelo card do Chatwoot.
                show: false,
                name: 'Chatwoot',
                key: 'integracoes-chatwoot',
                menuKey: 'integracoes',
                permissions: 'Integrations',
                route: '/configuracoes/integracoes/chatwoot',
                icon: <ExtensionIcon />,
                component: <ChatwootIntegration />,
            },
        ],
    },
    {
        type: 'collapse',
        name: t('sideNav.multicanal'),
        key: 'multicanal',
        icon: <ForumIcon />,
        collapse: [
            {
                name: t('sideNav.canais.todos'),
                key: 'multicanal-todos',
                permissions: 'Multichannel',
                route: '/multicanal',
                icon: <ForumIcon />,
                component: <Multichannel />,
            },
            {
                name: t('sideNav.canais.whatsapp'),
                key: 'multicanal-whatsapp',
                permissions: 'Multichannel',
                route: '/multicanal/whatsapp',
                icon: <WhatsAppIcon />,
                component: <Multichannel channel="whatsapp" />,
            },
            {
                name: t('sideNav.canais.instagram'),
                key: 'multicanal-instagram',
                permissions: 'Multichannel',
                route: '/multicanal/instagram',
                icon: <InstagramIcon />,
                component: <Multichannel channel="instagram" />,
            },
            {
                name: t('sideNav.canais.tiktok'),
                key: 'multicanal-tiktok',
                permissions: 'Multichannel',
                route: '/multicanal/tiktok',
                icon: <TikTokIcon />,
                component: <Multichannel channel="tiktok" />,
            },
            {
                name: t('sideNav.canais.email'),
                key: 'multicanal-email',
                permissions: 'Multichannel',
                route: '/multicanal/email',
                icon: <EmailIcon />,
                component: <Multichannel channel="email" />,
            },
            {
                name: t('sideNav.canais.site'),
                key: 'multicanal-site',
                permissions: 'Multichannel',
                route: '/multicanal/site',
                icon: <LanguageIcon />,
                component: <Multichannel channel="webwidget" />,
            },
        ],
    },
    {
        type: 'collapse',
        name: t('sideNav.sistema.sistema'),
        key: 'sistema',
        icon: <ComputerIcon />,
        collapse: [
            {
                name: t('sideNav.sistema.perfis'),
                key: 'perfis',
                permissions: 'Profiles',
                route: '/sistema/perfis',
                icon: <AccountBoxIcon />,
                component: <Profiles />,
            },
            {
                name: t('sideNav.sistema.usuarios'),
                key: 'usuarios',
                permissions: 'Users',
                route: '/sistema/usuarios',
                icon: <SupervisedUserCircleIcon />,
                component: <Users />,
            },
        ],
    },
];

export default routes;
